import React, { useEffect, useRef, useState } from 'react';

/**
 * Counts numeric prefix of `value` from 0 -> target when scrolled into view.
 * Preserves any trailing suffix like "+", "M", "K", "CXO".
 * Examples: "50+" -> counts 0..50 then "+", "300+" -> 0..300, "01" -> 0..1 padded.
 */
const CountUp = ({ value, duration = 1400, className = '' }) => {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);
  const [played, setPlayed] = useState(false);

  // Parse numeric prefix
  const match = String(value).match(/^(\d+)([^\d]*.*)?$/);
  const target = match ? parseInt(match[1], 10) : null;
  const suffix = match ? match[2] || '' : '';
  const padLen = match && match[1].startsWith('0') ? match[1].length : 0;

  useEffect(() => {
    if (target === null) {
      setDisplay(value);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !played) {
            setPlayed(true);
            const start = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              // ease-out cubic
              const eased = 1 - Math.pow(1 - t, 3);
              const current = Math.round(target * eased);
              const text = padLen
                ? String(current).padStart(padLen, '0')
                : String(current);
              setDisplay(text + suffix);
              if (t < 1) requestAnimationFrame(tick);
              else setDisplay(value);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [target, value, duration, played, suffix, padLen]);

  if (target === null) return <span className={className}>{value}</span>;
  return (
    <span ref={ref} className={className} aria-label={value}>
      {display}
    </span>
  );
};

export default CountUp;
